import { useState } from 'react';
import { toast } from 'sonner';
import { useMyCapabilities } from '../../utils/metadata/capabilities';
import './ResetColumnsButton.css';

interface SaveDefaultLayoutButtonProps {
  disabled?: boolean;
  /**
   * Saves the grid's current columns (order, which ones show, widths) as the
   * default layout everyone gets. Each grid engine passes its own writer:
   * WorkspaceGrid to core_crm.grid_column_default, TableGrid to the table's
   * Page Builder list layout (core_crm.table_ui_component), ReportGrid to the
   * report's columns (core_crm.report_field).
   */
  onSave: () => Promise<void>;
}

/**
 * REG-828: "Save as default layout", next to "Reset columns" on every grid
 * that saves a layout. Shown to holders of manage_default_layouts (the Dev
 * group; super admins pass), which every engine's write also checks. Each user's
 * own layout still wins for them, and "Reset columns" returns to this default.
 */
export function SaveDefaultLayoutButton({ disabled = false, onSave }: SaveDefaultLayoutButtonProps) {
  const { hasCapability } = useMyCapabilities();
  const [saving, setSaving] = useState(false);
  if (!hasCapability('manage_default_layouts')) return null;

  const handleClick = async () => {
    if (!window.confirm('Make these columns the default layout for everyone? People who arranged their own columns keep theirs.')) return;
    setSaving(true);
    try {
      await onSave();
      toast.success('Default layout saved for everyone.');
    } catch (error) {
      toast.error((error as Error)?.message || 'Could not save the default layout.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      type="button"
      className="sky-grid-reset-button"
      aria-label="Save as default layout"
      title="Make the current columns, order, visibility and widths everyone's default layout"
      disabled={disabled || saving}
      onClick={() => void handleClick()}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
        <path d="M17 21v-8H7v8" />
        <path d="M7 3v5h8" />
      </svg>
      {saving ? 'Saving...' : 'Save as default layout'}
    </button>
  );
}
